// The AI endpoints (scans, and the saves that follow them) start a background job and return
// { job_id } straight away, since the work can take longer than Heroku's 30-second request limit.
// runAiJob waits for the job and resolves like a normal axios response ({ data: result }); a failed
// job rejects like an axios error (error.response.data), so callers' existing success and error
// handling works unchanged. Jobs are kept server-side (ai_jobs), so a page can pick one up again later.
const POLL_INTERVAL = 3000
const MAX_POLL_ERRORS = 5

export async function runAiJob(vm, startRequest, { onStarted } = {}) {
  const jobId = (await startRequest).data.job_id
  if (onStarted) {
    onStarted(jobId)
  }
  return pollAiJob(vm, jobId)
}

// Waits for an already started job (e.g. one picked up again after leaving the page). Resolves with
// { data: result, review_state } where review_state is what the user has done with it (pending | applied | discarded).
export async function pollAiJob(vm, jobId) {
  let pollErrors = 0

  for (;;) {
    let job
    try {
      job = (await vm.$http.secured.get(`/app_ai_job/${jobId}`)).data
      pollErrors = 0
    } catch (error) {
      // A missing job won't come back; other failures (network blips) are retried a few times.
      if ((error.response && error.response.status === 404) || ++pollErrors >= MAX_POLL_ERRORS) {
        throw error
      }
      job = { status: 'running' }
    }

    if (job.status === 'done') {
      return { data: job.result, review_state: job.review_state }
    }
    if (job.status === 'failed') {
      const error = new Error((job.result && job.result.error) || 'Something went wrong. Please try again.')
      error.response = { data: job.result || {} }
      throw error
    }

    await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
    if (vm._isDestroyed) {
      throw new Error('Stopped waiting: the page was closed.')
    }
  }
}

// The most recent run of this kind on this menu that's still running or waiting for the user, or null.
export async function findOpenJob(vm, menuId, kind) {
  try {
    const response = await vm.$http.secured.get('/app_ai_jobs', { params: { menu_id: menuId, kind, unresolved: 1 } })
    return response.data.jobs[0] || null
  } catch (error) {
    return null
  }
}

// Keeps the job in the page address (?job=...), so reloading or coming back to the link finds it again.
export function rememberJob(vm, jobId) {
  if (vm.$route.query.job !== jobId) {
    vm.$router.replace({ query: { ...vm.$route.query, job: jobId } }).catch(() => {})
  }
}

export function forgetJob(vm) {
  if (vm.$route.query.job) {
    const query = { ...vm.$route.query }
    delete query.job
    vm.$router.replace({ query }).catch(() => {})
  }
}
